import {
  insertHeroSlideSchema,
  patchHeroSlideSchema,
} from "@/api/db/schema/hero-slide";
import { heroSlideService } from "@/api/services/hero-slide.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

/**
 * Public: List active slides for hero carousel
 */
export const listActiveSlidesFn = createServerFn({ method: "GET" }).handler(
  async () => {
    try {
      const slides = await heroSlideService.listActiveSlides();
      return { status: 200, body: { slides } };
    } catch (error) {
      console.error("Failed to fetch active slides:", error);
      return { status: 500, body: { slides: [] } };
    }
  },
);

export const listActiveSlidesOptions = () =>
  queryOptions({
    queryKey: ["hero-slides", "active"],
    queryFn: () => listActiveSlidesFn(),
  });

/**
 * Admin: List all slides (including inactive)
 */
export const listAllSlidesFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } };
    }

    try {
      const slides = await heroSlideService.listAllSlides();
      return { status: 200, body: { slides } };
    } catch (error) {
      console.error("Failed to fetch all slides:", error);
      return { status: 500, body: { error: "Failed to fetch slides" } };
    }
  });

export const listAllSlidesOptions = () =>
  queryOptions({
    queryKey: ["hero-slides", "all"],
    queryFn: () => listAllSlidesFn(),
  });

/**
 * Admin: Create new slide
 */
export const createSlideFn = createServerFn({ method: "POST" })
  .inputValidator(insertHeroSlideSchema)
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } };
    }

    try {
      const slide = await heroSlideService.createSlide(data);
      return { status: 201, body: { slide } };
    } catch (error) {
      console.error("Failed to create slide:", error);
      return { status: 500, body: { error: "Failed to create slide" } };
    }
  });

/**
 * Admin: Update slide
 */
export const updateSlideFn = createServerFn({ method: "POST" })
  .inputValidator(patchHeroSlideSchema)
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } };
    }

    try {
      const { slideId, ...updates } = data;
      const slide = await heroSlideService.updateSlide(slideId, updates);

      if (!slide) {
        return { status: 404, body: { error: "Slide not found" } };
      }

      return { status: 200, body: { slide } };
    } catch (error) {
      console.error("Failed to update slide:", error);
      return { status: 500, body: { error: "Failed to update slide" } };
    }
  });

/**
 * Admin: Delete slide
 */
export const deleteSlideFn = createServerFn({ method: "POST" })
  .inputValidator(z.object({ slideId: z.string() }))
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } };
    }

    try {
      const slide = await heroSlideService.deleteSlide(data.slideId);

      if (!slide) {
        return { status: 404, body: { error: "Slide not found" } };
      }

      return { status: 200, body: { message: "Slide deleted" } };
    } catch (error) {
      console.error("Failed to delete slide:", error);
      return { status: 500, body: { error: "Failed to delete slide" } };
    }
  });

/**
 * Admin: Toggle slide active status
 */
export const toggleSlideActiveFn = createServerFn({ method: "POST" })
  .inputValidator(z.object({ slideId: z.string() }))
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } };
    }

    try {
      const slide = await heroSlideService.toggleActive(data.slideId);

      if (!slide) {
        return { status: 404, body: { error: "Slide not found" } };
      }

      return { status: 200, body: { slide } };
    } catch (error) {
      console.error("Failed to toggle slide:", error);
      return { status: 500, body: { error: "Failed to toggle slide" } };
    }
  });
